import type { AnchoringRuleModel } from '@/shared/generated'
import type { ErrorSchema } from '@/shared/tools/json-schema-utils'

import React, { useMemo, useRef } from 'react'
import { pipe, prop, set } from 'lodash/fp'
import { HelpCircleIcon, PlusIcon, XIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	getAnchoringBehaviorChoices,
	getAnchoringTypeChoices,
} from '@/entities/pricing-campaign'

import { Button } from '@/shared/components/ui/button'
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput,
} from '@/shared/components/ui/input-group'
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/ui/select'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/shared/components/ui/table'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { ampli } from '@/shared/lib/ampli'
import {
	mapChangeEventValue,
	valueAsNumber,
} from '@/shared/tools/json-schema-utils'

const getErrorString = prop('message')
type AnchoringRulesProps = {
	formData: AnchoringRuleModel[]
	onChange: (value: AnchoringRuleModel[]) => void
	errorSchema: ErrorSchema
}

export const AnchoringRulesFileds = ({
	formData,
	onChange,
	errorSchema,
}: AnchoringRulesProps) => {
	const { t } = useTranslation()
	const addButtonRef = useRef<HTMLButtonElement>(null)
	const handleAddRule = () => {
		ampli.pricingSettingsAnchoringAddAnchorRule()
		onChange([
			...(formData || []),
			{
				anchoring: undefined,
				high_range: 0,
				high_range_behavior: undefined,
				low_range: 0,
				low_range_behavior: undefined,
			} as unknown as AnchoringRuleModel,
		])
	}

	const handleRemoveRule = (index: number) => {
		ampli.pricingSettingsAnchoringRemoveAnchorRule()
		onChange(formData?.filter((item, i) => i !== index) || [])
		// Focus the add button after deleting a rule
		addButtonRef.current?.focus()
	}
	const handleChange =
		(index: number) =>
		({
			target: { value, name },
		}: {
			target: { value: string | number; name: string }
		}) => {
			onChange(
				formData?.map((item, i) =>
					i === index ? { ...item, ...set(name, value, item) } : item
				) || []
			)
		}

	return (
		<>
			<div className='w-full border-b border-solid'>
				<Table className='table-fixed'>
					<TableHeader className='top-auto!'>
						<TableRow>
							<TableHead className='bg-transparent p-2 text-xs'>
								{t('anchoring.field.anchoring.label', {
									defaultValue: 'Anchor type',
								})}{' '}
							</TableHead>
							<TableHead className='w-36 bg-transparent p-2 text-xs'>
								{t('anchoring.field.low_range.label', {
									defaultValue: 'Low range',
								})}
								<Tooltip>
									<TooltipTrigger asChild>
										<HelpCircleIcon className='ml-1 inline-flex size-3' />
									</TooltipTrigger>
									<TooltipContent>
										{t('anchoring.field.low_range.help', {
											defaultValue: 'Low range help',
										})}
									</TooltipContent>
								</Tooltip>
							</TableHead>
							<TableHead className='w-52 bg-transparent p-2 text-xs'>
								{t('anchoring.field.low_range_behavior.label', {
									defaultValue: 'Low range behavior',
								})}
								<Tooltip>
									<TooltipTrigger asChild>
										<HelpCircleIcon className='ml-1 inline-flex size-3' />
									</TooltipTrigger>
									<TooltipContent>
										{t('anchoring.field.low_range_behavior.help', {
											defaultValue: 'Low range behavior help',
										})}
									</TooltipContent>
								</Tooltip>
							</TableHead>
							<TableHead className='w-36 bg-transparent p-2 text-xs'>
								{t('anchoring.field.high_range.label', {
									defaultValue: 'High range',
								})}
								<Tooltip>
									<TooltipTrigger asChild>
										<HelpCircleIcon className='ml-1 inline-flex size-3' />
									</TooltipTrigger>
									<TooltipContent>
										{t('anchoring.field.high_range.help', {
											defaultValue: 'High range help',
										})}
									</TooltipContent>
								</Tooltip>
							</TableHead>
							<TableHead className='w-52 bg-transparent p-2 text-xs'>
								{t('anchoring.field.high_range_behavior.label', {
									defaultValue: 'High range behavior',
								})}
								<Tooltip>
									<TooltipTrigger asChild>
										<HelpCircleIcon className='ml-1 inline-flex size-3' />
									</TooltipTrigger>
									<TooltipContent>
										{t('anchoring.field.high_range_behavior.help', {
											defaultValue: 'High range behavior help',
										})}
									</TooltipContent>
								</Tooltip>
							</TableHead>
							<TableHead className='w-10 bg-transparent p-2 text-xs' />
						</TableRow>
					</TableHeader>
					<TableBody>
						{formData?.length > 0 ? (
							formData?.map((rule, index) => (
								<AnchoringRule
									errorSchema={errorSchema?.[index]}
									onChange={handleChange(index)}
									key={index}
									rule={rule}
									onRemove={() => handleRemoveRule(index)}
								/>
							))
						) : (
							<TableRow>
								<TableCell colSpan={6} className='border-none'>
									<div className='p-5 text-center text-sm text-muted-foreground'>
										{t('general_no_data', {
											defaultValue: 'No data',
										})}
									</div>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>
			<Button
				data-testid='add-anchoring-rule-button'
				ref={addButtonRef}
				variant='ghost'
				onClick={handleAddRule}
			>
				<PlusIcon />
				{t('app.add_anchor', {
					defaultValue: 'Add anchors set',
				})}
			</Button>
		</>
	)
}

type AnchoringRuleProps = {
	rule: AnchoringRuleModel
	onChange: (value: {
		target: { name: string; value: string | number }
	}) => void
	errorSchema: Partial<AnchoringRuleModel>
	onRemove: () => void
}
function AnchoringRule({
	errorSchema,
	onChange,
	rule,
	onRemove,
}: AnchoringRuleProps) {
	const { t } = useTranslation()
	const anchoringError = getErrorString(errorSchema?.anchoring)
	const highRangeError = getErrorString(errorSchema?.high_range)
	const highRangeBehaviorError = getErrorString(
		errorSchema?.high_range_behavior
	)
	const lowRangeError = getErrorString(errorSchema?.low_range)
	const lowRangeBehaviorError = getErrorString(errorSchema?.low_range_behavior)

	const { anchoringBehaviorChoices, anchoringTypeChoices } = useMemo(
		() => ({
			anchoringBehaviorChoices: getAnchoringBehaviorChoices(t),
			anchoringTypeChoices: getAnchoringTypeChoices(t),
		}),
		[]
	)
	const handleSelectChange = (name: string) => (value: string) =>
		onChange({ target: { name, value } })

	return (
		<TableRow>
			<TableCell className='border-none p-2 align-top'>
				<div className='mb-0'>
					<Select
						value={rule.anchoring}
						onValueChange={handleSelectChange('anchoring')}
					>
						<SelectTrigger
							className='w-full'
							name='anchoring'
							data-name='anchoring'
							aria-invalid={Boolean(anchoringError)}
						>
							<SelectValue
								placeholder={t('general_select', {
									defaultValue: 'Select',
								})}
							/>
						</SelectTrigger>
						<SelectContent>
							<SelectGroup>
								{anchoringTypeChoices.map((choice) => (
									<SelectItem
										key={choice.value}
										value={choice.value}
										data-value={choice.value}
									>
										{choice.label}
									</SelectItem>
								))}
							</SelectGroup>
						</SelectContent>
					</Select>
					{anchoringError && (
						<p className='text-sm text-destructive'>{anchoringError}</p>
					)}
				</div>
			</TableCell>
			<TableCell className='border-none p-2 align-top'>
				<div className='mb-0'>
					<InputGroup className='w-32'>
						<InputGroupInput
							name='low_range'
							type='number'
							aria-invalid={lowRangeError}
							autoComplete='off'
							autoCorrect='off'
							value={rule.low_range}
							onChange={pipe([mapChangeEventValue(valueAsNumber), onChange])}
						/>
						<InputGroupAddon align='inline-end'>%</InputGroupAddon>
					</InputGroup>
					{lowRangeError && (
						<p className='text-sm text-destructive'>{lowRangeError}</p>
					)}
				</div>
			</TableCell>
			<TableCell className='border-none p-2 align-top'>
				<div className='mb-0 w-full'>
					<Select
						value={rule.low_range_behavior}
						onValueChange={handleSelectChange('low_range_behavior')}
					>
						<SelectTrigger
							className='w-full'
							name='type'
							data-name='type'
							aria-invalid={!!lowRangeBehaviorError}
						>
							<SelectValue
								placeholder={t('general_select', {
									defaultValue: 'Select',
								})}
							/>
						</SelectTrigger>
						<SelectContent>
							<SelectGroup>
								{anchoringBehaviorChoices.map((choice) => (
									<SelectItem key={choice.value} value={choice.value}>
										{choice.label}
									</SelectItem>
								))}
							</SelectGroup>
						</SelectContent>
					</Select>
					{lowRangeBehaviorError && (
						<p className='text-sm text-destructive'>{lowRangeBehaviorError}</p>
					)}
				</div>
			</TableCell>
			<TableCell className='border-none p-2 align-top'>
				<div className='mb-0'>
					<InputGroup className='w-32'>
						<InputGroupInput
							name='high_range'
							type='number'
							aria-invalid={highRangeError}
							autoComplete='off'
							autoCorrect='off'
							value={rule.high_range}
							onChange={pipe([mapChangeEventValue(valueAsNumber), onChange])}
						/>
						<InputGroupAddon align='inline-end'>%</InputGroupAddon>
					</InputGroup>

					{highRangeError && (
						<p className='text-sm text-destructive'>{highRangeError}</p>
					)}
				</div>
			</TableCell>
			<TableCell className='overflow-hidden border-none p-2 align-top'>
				<div className='mb-0 w-full'>
					<Select
						value={rule.high_range_behavior}
						onValueChange={handleSelectChange('high_range_behavior')}
					>
						<SelectTrigger
							className='w-full'
							name='type'
							aria-invalid={!!highRangeBehaviorError}
						>
							<SelectValue
								placeholder={t('general_select', {
									defaultValue: 'Select',
								})}
							/>
						</SelectTrigger>
						<SelectContent>
							<SelectGroup>
								{anchoringBehaviorChoices.map((choice) => (
									<SelectItem
										key={choice.value}
										value={choice.value}
										data-value={choice.value}
									>
										{choice.label}
									</SelectItem>
								))}
							</SelectGroup>
						</SelectContent>
					</Select>

					{!!highRangeBehaviorError && (
						<p className='line-clamp-3 text-sm text-destructive'>
							{highRangeBehaviorError}
						</p>
					)}
				</div>
			</TableCell>
			<TableCell className='overflow-hidden border-none p-0 align-top'>
				<Button
					data-testid='remove-anchoring-rule-button'
					className='mt-2'
					variant='ghost'
					size='icon'
					onClick={onRemove}
				>
					<XIcon />
				</Button>
			</TableCell>
		</TableRow>
	)
}
